import { useState, useRef, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import { formatApiError, sanitizeHtmlBasico, markdownToHtml, pareceHtml } from '@/lib/utils';
import { Send, Sparkles, Loader2, User, Plus, MessageSquare, Trash2 } from 'lucide-react';

// La IA responde en Markdown (a veces HTML). Renderizamos a HTML seguro en ambos casos.
const renderIA = (texto) => sanitizeHtmlBasico(pareceHtml(texto) ? texto : markdownToHtml(texto));

const fmtFecha = (s) => {
  try { return new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }); }
  catch { return ''; }
};

export default function Asistente() {
  const [mensajes, setMensajes] = useState([]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [convId, setConvId] = useState(null);
  const [perfil, setPerfil] = useState('abogado');
  const [convs, setConvs] = useState([]);
  const [cargandoConvs, setCargandoConvs] = useState(true);
  const [cargandoMsgs, setCargandoMsgs] = useState(false);
  const [cupo, setCupo] = useState(null);
  const finRef = useRef(null);

  useEffect(() => { finRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, enviando]);

  const cargarCupo = useCallback(async () => {
    try { const r = await api.get('/api/asistente/estado'); setCupo(r.data); } catch { /* noop */ }
  }, []);
  useEffect(() => { cargarCupo(); }, [cargarCupo]);

  const cargarConvs = useCallback(async () => {
    try {
      const r = await api.get('/api/asistente/conversaciones');
      setConvs(r.data.conversaciones || []);
    } catch { /* silencioso */ } finally { setCargandoConvs(false); }
  }, []);
  useEffect(() => { cargarConvs(); }, [cargarConvs]);

  const nuevoChat = () => { setConvId(null); setMensajes([]); setInput(''); };

  const abrirConv = async (c) => {
    if (c.id === convId) return;
    setCargandoMsgs(true);
    try {
      const r = await api.get(`/api/asistente/conversaciones/${c.id}`);
      setMensajes((r.data.mensajes || []).map((m) => ({ rol: m.rol, contenido: m.contenido })));
      setConvId(c.id);
    } catch (err) {
      showAlert(formatApiError(err.response?.data?.detail, 'No se pudo abrir la conversación.'));
    } finally { setCargandoMsgs(false); }
  };

  const eliminarConv = async (e, c) => {
    e.stopPropagation();
    if (!(await showConfirm('¿Eliminar esta conversación? No se puede deshacer.', { titulo: 'Eliminar conversación' }))) return;
    try {
      await api.delete(`/api/asistente/conversaciones/${c.id}`);
      if (c.id === convId) nuevoChat();
      cargarConvs();
    } catch (err) {
      showAlert(formatApiError(err.response?.data?.detail, 'No se pudo eliminar.'));
    }
  };

  const enviar = async (e) => {
    e.preventDefault();
    const texto = input.trim();
    if (!texto || enviando) return;
    setInput('');
    setMensajes((m) => [...m, { rol: 'user', contenido: texto }]);
    setEnviando(true);
    try {
      const r = await api.post('/api/asistente/chat', {
        mensaje: texto, conversacion_id: convId, perfil_respuesta: perfil,
      });
      setConvId(r.data.conversacion_id);
      setMensajes((m) => [...m, { rol: 'assistant', contenido: r.data.respuesta }]);
      cargarConvs(); // refresca títulos/orden y agrega la nueva si recién se creó
      cargarCupo();  // actualiza el contador de consultas del estudio
    } catch (err) {
      const det = formatApiError(err.response?.data?.detail, 'Error al consultar el asistente');
      setMensajes((m) => [...m, { rol: 'assistant', contenido: `⚠️ ${det}`, error: true }]);
    } finally { setEnviando(false); }
  };

  return (
    <DashboardLayout title="Asistente IA">
      <div className="flex h-[calc(100vh-9rem)] bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden">

        {/* ── Historial (izquierda) ── */}
        <div className="w-60 border-r border-[rgba(201,164,76,0.12)] flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-[rgba(201,164,76,0.12)]">
            <button onClick={nuevoChat}
              className="w-full flex items-center justify-center gap-2 text-[#070707] text-sm font-semibold py-2.5 rounded-lg transition-all hover:shadow-[0_0_20px_rgba(201,164,76,0.3)]"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              <Plus className="w-4 h-4" /> Nuevo chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-[#B8B8B8]/40">Historial</div>
            {cargandoConvs ? (
              <div className="flex justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-[#C9A44C]" /></div>
            ) : convs.length === 0 ? (
              <p className="text-[11px] text-[#666] px-3 py-2">Tus conversaciones van a aparecer acá.</p>
            ) : convs.map((c) => (
              <button key={c.id} onClick={() => abrirConv(c)}
                className={`group w-full text-left px-3 py-2.5 border-b border-white/5 transition-all flex items-center gap-2 ${convId === c.id ? 'bg-[#C9A44C]/10' : 'hover:bg-white/[0.03]'}`}>
                <MessageSquare className={`w-3.5 h-3.5 flex-shrink-0 ${convId === c.id ? 'text-[#C9A44C]' : 'text-[#777]'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] truncate text-[#E8E4DA]">{c.titulo || 'Consulta'}</div>
                  <div className="text-[10px] text-[#777]">{fmtFecha(c.actualizado_en)}</div>
                </div>
                <span onClick={(e) => eliminarConv(e, c)}
                  className="opacity-0 group-hover:opacity-100 text-[#777] hover:text-red-400 transition-all flex-shrink-0" title="Eliminar">
                  <Trash2 className="w-3.5 h-3.5" />
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Chat (derecha) ── */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="px-5 py-3 border-b border-[rgba(201,164,76,0.12)] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#C9A44C]" />
              <span className="text-sm">Asistente legal — derecho argentino</span>
            </div>
            <div className="flex items-center gap-3">
            {cupo?.cupo_mensual != null && (
              <span className="text-[11px] text-[#888] whitespace-nowrap"
                title={cupo.renueva_texto ? `Se renuevan el ${cupo.renueva_texto}` : ''}>
                Consultas IA:{' '}
                <strong className={cupo.restantes <= Math.max(3, Math.round(cupo.cupo_mensual * 0.1)) ? 'text-amber-400' : 'text-[#E0C878]'}>
                  {cupo.usado_mes}/{cupo.cupo_mensual}
                </strong>
              </span>
            )}
            <select value={perfil} onChange={(e) => setPerfil(e.target.value)}
              className="bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#C9A44C]">
              <option value="abogado" className="bg-[#141414]">Perfil: Abogado</option>
              <option value="cliente" className="bg-[#141414]">Perfil: Cliente</option>
              <option value="estrategico" className="bg-[#141414]">Perfil: Estratégico</option>
            </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {cargandoMsgs ? (
              <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#C9A44C]" /></div>
            ) : mensajes.length === 0 ? (
              <div className="text-center text-[#666] py-16">
                <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Consultá sobre legislación, redactá escritos o analizá estrategias.</p>
                <p className="text-[11px] text-[#555] mt-2">Tip: usá <b className="text-[#888]">Nuevo chat</b> para cada tema, así no se mezclan las consultas.</p>
              </div>
            ) : mensajes.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.rol === 'user' ? 'justify-end' : ''}`}>
                {m.rol === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/30 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-[#C9A44C]" />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.rol === 'user'
                    ? 'bg-[#C9A44C] text-[#070707] whitespace-pre-wrap'
                    : m.error ? 'bg-red-500/10 border border-red-500/25 text-red-300 whitespace-pre-wrap' : 'bg-white/[0.04] border border-white/10 text-[#E8E4DA]'
                }`}>
                  {m.rol === 'assistant' && !m.error
                    ? <div className="ia-html" dangerouslySetInnerHTML={{ __html: renderIA(m.contenido) }} />
                    : m.contenido}
                </div>
                {m.rol === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-[#999]" />
                  </div>
                )}
              </div>
            ))}
            {enviando && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#C9A44C]/15 border border-[#C9A44C]/30 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#C9A44C]" />
                </div>
                <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#C9A44C]" /></div>
              </div>
            )}
            <div ref={finRef} />
          </div>

          <form onSubmit={enviar} className="p-4 border-t border-[rgba(201,164,76,0.12)] flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribí tu consulta..."
              className="flex-1 bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C]" />
            <button type="submit" disabled={enviando || !input.trim()}
              className="text-[#070707] px-4 rounded-lg flex items-center justify-center disabled:opacity-40 transition-all"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}
